import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Camera, X, Loader2, QrCode } from 'lucide-react';

/**
 * QRScanner Component
 * 
 * A reusable QR code scanner using html5-qrcode library.
 * Supports camera scanning with fallback to manual input.
 * 
 * @param {function} onScan - Callback when QR code is scanned (receives code string)
 * @param {function} onClose - Callback to close the scanner
 * @param {string} placeholder - Placeholder text for manual input
 * @param {boolean} showManualInput - Whether to show manual input option
 */
const QRScanner = ({ 
  onScan, 
  onClose, 
  placeholder = "Entrez le code manuellement...",
  showManualInput = true 
}) => {
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);
  const [manualCode, setManualCode] = useState('');
  const [cameraPermission, setCameraPermission] = useState('pending'); // pending, granted, denied

  useEffect(() => {
    // Initialize scanner
    const scannerId = `qr-scanner-${Date.now()}`;
    
    // Create container if it doesn't exist
    if (scannerRef.current) {
      scannerRef.current.id = scannerId;
    }

    return () => {
      // Cleanup scanner on unmount
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
        html5QrCodeRef.current.clear();
      }
    };
  }, []);

  const startScanning = async () => {
    setError(null);
    setIsScanning(true);

    try {
      const scannerId = scannerRef.current?.id;
      if (!scannerId) {
        throw new Error('Scanner container not found');
      }

      html5QrCodeRef.current = new Html5Qrcode(scannerId);

      // Check for cameras
      const cameras = await Html5Qrcode.getCameras();
      
      if (!cameras || cameras.length === 0) {
        setCameraPermission('denied');
        setError('Aucune caméra détectée. Utilisez la saisie manuelle.');
        setIsScanning(false);
        return;
      }

      setCameraPermission('granted');

      // Prefer back camera for mobile devices
      const backCamera = cameras.find(
        camera => camera.label.toLowerCase().includes('back') || 
                  camera.label.toLowerCase().includes('arrière') ||
                  camera.label.toLowerCase().includes('rear')
      );
      const cameraId = backCamera?.id || cameras[0].id;

      await html5QrCodeRef.current.start(
        cameraId,
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
        },
        (decodedText) => {
          // Success callback
          handleScanSuccess(decodedText);
        },
        (errorMessage) => {
          // Error callback (scanning in progress, not a real error)
          // This fires continuously while scanning, so we ignore it
        }
      );

    } catch (err) {
      console.error('QR Scanner error:', err);
      setCameraPermission('denied');
      
      if (err.message?.includes('Permission')) {
        setError('Accès caméra refusé. Autorisez l\'accès ou utilisez la saisie manuelle.');
      } else if (err.message?.includes('NotFoundError')) {
        setError('Aucune caméra trouvée. Utilisez la saisie manuelle.');
      } else {
        setError('Erreur initialisation caméra. Utilisez la saisie manuelle.');
      }
      
      setIsScanning(false);
    }
  };

  const stopScanning = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
      } catch (err) {
        // Ignore stop errors
      }
    }
    setIsScanning(false);
  };

  const handleScanSuccess = async (code) => {
    // Stop scanning
    await stopScanning();
    
    // Callback with scanned code
    onScan?.(code);
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualCode.trim()) {
      onScan?.(manualCode.trim().toUpperCase());
    }
  };

  const handleClose = async () => {
    await stopScanning();
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
      <div 
        className="bg-[var(--bg2)] rounded-xl border border-[var(--border)] w-full max-w-md mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--border)]">
          <div className="flex items-center gap-2">
            <QrCode size={20} className="text-[var(--accent)]" />
            <span className="font-bold">Scanner QR Code</span>
          </div>
          <button 
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-[var(--bg3)] transition-colors"
            data-testid="qr-scanner-close"
          >
            <X size={20} className="text-[var(--text3)]" />
          </button>
        </div>

        {/* Scanner Area */}
        <div className="p-4">
          {/* Camera View */}
          <div 
            ref={scannerRef}
            className="w-full aspect-square bg-black rounded-lg overflow-hidden mb-4"
            style={{ minHeight: '280px' }}
          >
            {!isScanning && cameraPermission !== 'denied' && (
              <div className="w-full h-full flex flex-col items-center justify-center text-[var(--text3)]">
                <Camera size={48} className="mb-4 opacity-50" />
                <button
                  onClick={startScanning}
                  className="btn btn-primary"
                  data-testid="qr-scanner-start"
                >
                  <Camera size={14} /> Activer la caméra
                </button>
              </div>
            )}
            
            {isScanning && (
              <div className="w-full h-full flex items-center justify-center">
                <Loader2 size={32} className="animate-spin text-[var(--accent)]" />
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 mb-4 rounded-lg bg-[var(--red-dim)] text-[var(--red)] text-sm">
              {error}
            </div>
          )}

          {/* Scanning Status */}
          {isScanning && (
            <div className="text-center mb-4">
              <div className="text-sm text-[var(--text2)]">
                Pointez la caméra vers le QR code...
              </div>
              <button
                onClick={stopScanning}
                className="btn btn-ghost btn-sm mt-2"
              >
                Arrêter le scan
              </button>
            </div>
          )}

          {/* Manual Input */}
          {showManualInput && (
            <div className="border-t border-[var(--border)] pt-4">
              <div className="text-xs text-[var(--text3)] uppercase mb-2">
                Ou saisir manuellement
              </div>
              <form onSubmit={handleManualSubmit} className="flex gap-2">
                <input
                  type="text"
                  className="input flex-1"
                  placeholder={placeholder}
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value)}
                  data-testid="qr-scanner-manual-input"
                />
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!manualCode.trim()}
                  data-testid="qr-scanner-manual-submit"
                >
                  OK
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRScanner;
